import { isNotEmpty } from '@dealstack/shared/lib/isNotEmpty.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useMutation, useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import {
	createManyCustomDetailDefinitionForPreFillableFormTemplate,
	deleteCustomDetailsDefinitions,
	getPreFillableFormTemplateCustomDetailsDefinitions,
	updateCustomDetailsDefinitions,
} from '@dealstack/frontend/shared/dataExchange/services/god.service.ts';
import { ManageCustomDetailsDefinitionsForm } from '@dealstack/frontend/admin/pages/Manage/common/ManageCustomDetailsDefinitionsModal/types';
import { useState } from 'react';

export const useFormTemplateCustomDetailsDefinitions = (templateId: string) => {
	const geCustomDetailsDefinitionsRequest = useQuery({
		queryKey: [
			API_PATHS.GODMODE_PRE_FILLABLE_FORM_TEMPLATE_ID_CUSTOM_DETAILS_DEFINITIONS_GET,
			{ id: templateId },
		],

		queryFn: ({ signal }) =>
			getPreFillableFormTemplateCustomDetailsDefinitions(
				API_PATHS.GODMODE_PRE_FILLABLE_FORM_TEMPLATE_ID_CUSTOM_DETAILS_DEFINITIONS_GET,
				{ id: templateId },
				signal
			),
	});

	const customDetailsDefinitions =
		geCustomDetailsDefinitionsRequest?.data?.payload?.customDetailDefinitions;

	const createCustomDetailsDefinitionsRequest = useMutation({
		mutationKey: [API_PATHS.GODMODE_PRE_FILLABLE_FORM_TEMPLATE_CUSTOM_DETAILS_DEFINITIONS_POST],
		mutationFn: createManyCustomDetailDefinitionForPreFillableFormTemplate,
	});
	const updateCustomDetailsDefinitionsRequest = useMutation({
		mutationKey: [API_PATHS.GODMODE_PRE_FILLABLE_FORM_TEMPLATE_CUSTOM_DETAILS_DEFINITIONS_PUT],
		mutationFn: updateCustomDetailsDefinitions,
	});
	const deleteCustomDetailsDefinitionsRequest = useMutation({
		mutationKey: [
			API_PATHS.GODMODE_PRE_FILLABLE_FORM_TEMPLATE_CUSTOM_DETAILS_DEFINITIONS_DELETE,
		],
		mutationFn: deleteCustomDetailsDefinitions,
	});

	const [showManageCustomDetailsDefinitionsModal, setShowManageCustomDetailsDefinitionsModal] =
		useState(false);

	const onCustomDetailsDefinitionsSubmit = async (
		formValues: ManageCustomDetailsDefinitionsForm
	) => {
		const formCustomDetailDefinitions = formValues.customDefinitions;

		let isAnyFailed = false;
		const definitionsToCreate = formCustomDetailDefinitions
			.filter((def) => !!def.localId)
			.map((def) => ({
				id: def.id as string,
				name: def.name,
				instruction: def.instruction,
			}));
		const definitionsToUpdate = formCustomDetailDefinitions
			.filter((def) => {
				const originalDefinition = customDetailsDefinitions?.find(
					(initialDef) => initialDef.id === def.id
				);
				return (
					!!originalDefinition?.id &&
					(originalDefinition.name !== def.name ||
						originalDefinition.instruction !== def.instruction)
				);
			})
			.map((def) => ({
				id: def.id as string,
				name: def.name,
				instruction: def.instruction,
			}));
		const definitionsToDelete = customDetailsDefinitions
			?.filter((initialDef) => {
				const localDefinition = formCustomDetailDefinitions.find(
					(def) => initialDef.id === def.id
				);
				return !localDefinition;
			})
			.map((def) => def.id as string);

		await Promise.allSettled([
			isNotEmpty(definitionsToCreate)
				? createCustomDetailsDefinitionsRequest.mutateAsync(
						{
							customDetailsDefinitions: definitionsToCreate,
							formTemplateId: templateId,
						},
						{
							onError: () => {
								isAnyFailed = true;
							},
						}
					)
				: null,
			isNotEmpty(definitionsToUpdate)
				? updateCustomDetailsDefinitionsRequest.mutateAsync(
						{ customDetailsDefinitions: definitionsToUpdate },
						{
							onError: () => {
								isAnyFailed = true;
							},
						}
					)
				: null,
			isNotEmpty(definitionsToDelete)
				? deleteCustomDetailsDefinitionsRequest.mutateAsync(
						{ customDetailsDefinitionsIds: definitionsToDelete },
						{
							onError: () => {
								isAnyFailed = true;
							},
						}
					)
				: null,
		]);

		if (isAnyFailed) {
			showToastV2({ state: 'error', message: 'Failed to save data' });
			return;
		}

		showToastV2({ message: 'Saved' });
		await geCustomDetailsDefinitionsRequest.refetch();
		setShowManageCustomDetailsDefinitionsModal(false);
	};

	return {
		onCustomDetailsDefinitionsSubmit,
		customDetailsDefinitions,
		showManageCustomDetailsDefinitionsModal,
		setShowManageCustomDetailsDefinitionsModal,
	};
};
